import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, Calendar, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const AnimatedAIMockup: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [isVisible, setIsVisible] = useState(false);
  const [messages, setMessages] = useState<Array<{ type: 'lead' | 'ai'; text: string; visible: boolean }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentTypingText, setCurrentTypingText] = useState('');
  const [showBookingConfirm, setShowBookingConfirm] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Theme-aware colors
  const containerBg = isDark ? '#1A1A1A' : '#F8F8F8';
  const chatBg = isDark ? '#111111' : '#FFFFFF';
  const inputAreaBg = isDark ? '#1A1A1A' : '#F8F8F8';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E5';
  const borderColorLight = isDark ? 'rgba(255,255,255,0.08)' : '#E0E0E0';
  const textPrimary = isDark ? '#FFFFFF' : '#111111';
  const textSecondary = isDark ? '#AAAAAA' : '#555555';
  const textMuted = isDark ? '#666666' : '#888888';
  const aiBubbleBg = isDark ? '#2A2A2A' : '#F8F8F8';
  const aiBubbleText = isDark ? '#FFFFFF' : '#333333';
  const aiBubbleBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E0E0E0';
  const leadBubbleBg = isDark ? '#FFFFFF' : '#111111';
  const leadBubbleText = isDark ? '#111111' : '#FFFFFF';
  const userAvatarBg = isDark ? '#333333' : '#E0E0E0';
  const confirmOverlayBg = isDark ? 'rgba(17, 17, 17, 0.98)' : 'rgba(255, 255, 255, 0.98)';
  const confirmTextColor = isDark ? '#FFFFFF' : '#111111';
  const confirmCardBg = isDark ? '#1A3A2A' : '#F0FDF4';
  const confirmCardBorder = isDark ? '#22C55E' : '#86EFAC';
  const badgeBg = isDark ? '#2A2A2A' : '#FFFFFF';
  const badgeBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E0E0E0';
  const inputBg = isDark ? '#2A2A2A' : '#FFFFFF';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E0E0E0';
  const inputText = isDark ? '#FFFFFF' : '#333333';
  const inputPlaceholder = isDark ? '#666666' : '#888888';

  const conversation = [
    { type: 'lead' as const, text: "Hi, I'm interested in your marketing services" },
    { type: 'ai' as const, text: "Hi! Thanks for reaching out. I'd love to learn more about your business. What's your main marketing goal right now?" },
    { type: 'lead' as const, text: "We need more qualified leads for our SaaS product" },
    { type: 'ai' as const, text: "Perfect! We specialize in B2B SaaS lead generation. I have availability tomorrow at 2pm or Thursday at 10am for a quick strategy call. Which works better?" },
    { type: 'lead' as const, text: "Thursday at 10am works great" },
    { type: 'ai' as const, text: "Excellent! You're all set for Thursday at 10am. I'll send a calendar invite to your email. Looking forward to helping you grow! 🚀" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, currentTypingText]);

  // Reset and restart animation
  useEffect(() => {
    if (!isVisible) return;

    const restartAnimation = () => {
      setMessages([]);
      setCurrentTypingText('');
      setIsTyping(false);
      setShowBookingConfirm(false);
      setAnimationKey(prev => prev + 1);
    };

    // Restart animation every 18 seconds
    const restartInterval = setInterval(restartAnimation, 18000);

    return () => clearInterval(restartInterval);
  }, [isVisible]);

  // Run conversation animation
  useEffect(() => {
    if (!isVisible) return;

    setMessages([]);
    setShowBookingConfirm(false);
    
    let messageIndex = 0;
    let timeoutIds: NodeJS.Timeout[] = [];
    
    const showNextMessage = () => {
      if (messageIndex >= conversation.length) {
        const confirmTimeout = setTimeout(() => setShowBookingConfirm(true), 500);
        timeoutIds.push(confirmTimeout);
        return;
      }

      const currentMessage = conversation[messageIndex];
      
      if (currentMessage.type === 'ai') {
        setIsTyping(true);
        let charIndex = 0;
        const fullText = currentMessage.text;
        setCurrentTypingText('');
        
        const startTypingTimeout = setTimeout(() => {
          const typingInterval = setInterval(() => {
            charIndex++;
            setCurrentTypingText(fullText.substring(0, charIndex));
            
            if (charIndex >= fullText.length) {
              clearInterval(typingInterval);
              setIsTyping(false);
              setCurrentTypingText('');
              setMessages(prev => [...prev, { ...currentMessage, visible: true }]);
              messageIndex++;
              const nextTimeout = setTimeout(showNextMessage, 1000);
              timeoutIds.push(nextTimeout);
            }
          }, 20);
        }, 500);
        timeoutIds.push(startTypingTimeout);
      } else {
        setMessages(prev => [...prev, { ...currentMessage, visible: true }]);
        messageIndex++;
        const nextTimeout = setTimeout(showNextMessage, 800);
        timeoutIds.push(nextTimeout);
      }
    };

    const initialTimeout = setTimeout(showNextMessage, 800);
    timeoutIds.push(initialTimeout);

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [isVisible, animationKey]);

  return (
    <div ref={sectionRef} className="w-full max-w-md mx-auto">
      <div 
        className="rounded-xl overflow-hidden relative"
        style={{ 
          backgroundColor: containerBg, 
          border: `1px solid ${borderColor}`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          transition: 'all 0.6s ease-out'
        }}
      >
        {/* Header */}
        <div 
          className="px-4 py-3 flex items-center justify-between"
          style={{ borderBottom: `1px solid ${borderColor}` }}
        >
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111' }}
            >
              <MessageSquare className="w-4 h-4" style={{ color: isDark ? '#111111' : '#FFFFFF' }} />
            </div>
            <div>
              <span className="text-sm font-medium" style={{ color: textPrimary }}>AI Sales Agent</span>
              <div className="flex items-center gap-1">
                <div 
                  className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                  style={{ animation: 'pulse 2s ease-in-out infinite' }}
                />
                <span className="text-[10px]" style={{ color: textMuted }}>Online</span>
              </div>
            </div>
          </div>
          <div 
            className="flex items-center gap-1 px-2 py-1 rounded-full" 
            style={{ backgroundColor: badgeBg, border: `1px solid ${badgeBorder}` }}
          >
            <Bot className="w-3 h-3" style={{ color: textSecondary }} />
            <span className="text-[10px] font-medium" style={{ color: textPrimary }}>Lead Booking</span>
          </div>
        </div>

        {/* Chat Area - Fixed Height with Scroll */}
        <div 
          ref={chatContainerRef}
          className="p-4 h-[280px] overflow-y-auto space-y-3" 
          style={{ backgroundColor: chatBg }}
        >
          {messages.map((message, index) => (
            <div 
              key={index}
              className={`flex gap-2 ${message.type === 'lead' ? 'justify-end' : 'justify-start'}`}
              style={{
                opacity: message.visible ? 1 : 0,
                transform: message.visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.3s ease-out'
              }}
            >
              {message.type === 'ai' && (
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111' }}
                >
                  <Bot className="w-3.5 h-3.5" style={{ color: isDark ? '#111111' : '#FFFFFF' }} />
                </div>
              )}
              <div 
                className="max-w-[75%] px-3 py-2 rounded-xl text-xs leading-relaxed"
                style={message.type === 'lead' 
                  ? { 
                      backgroundColor: leadBubbleBg, 
                      color: leadBubbleText,
                      borderBottomRightRadius: '2px'
                    } 
                  : { 
                      backgroundColor: aiBubbleBg, 
                      color: aiBubbleText,
                      border: `1px solid ${aiBubbleBorder}`,
                      borderBottomLeftRadius: '2px'
                    }
                }
              >
                {message.text}
              </div>
              {message.type === 'lead' && (
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: userAvatarBg }}
                >
                  <User className="w-3.5 h-3.5" style={{ color: textSecondary }} />
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex gap-2 justify-start">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111' }}
              >
                <Bot className="w-3.5 h-3.5" style={{ color: isDark ? '#111111' : '#FFFFFF' }} />
              </div>
              <div 
                className="max-w-[75%] px-3 py-2 rounded-xl text-xs leading-relaxed"
                style={{ 
                  backgroundColor: aiBubbleBg, 
                  color: aiBubbleText,
                  border: `1px solid ${aiBubbleBorder}`,
                  borderBottomLeftRadius: '2px'
                }}
              >
                {currentTypingText || (
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: textMuted, animation: 'bounce 1s ease-in-out infinite' }} />
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: textMuted, animation: 'bounce 1s ease-in-out 0.1s infinite' }} />
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: textMuted, animation: 'bounce 1s ease-in-out 0.2s infinite' }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Booking confirmation - Prominent overlay */}
        {showBookingConfirm && (
          <div 
            className="absolute inset-0 flex items-center justify-center z-20 rounded-xl"
            style={{
              backgroundColor: confirmOverlayBg,
              animation: 'fadeIn 0.3s ease-out'
            }}
          >
            {/* Confetti burst */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
              {[...Array(24)].map((_, i) => {
                const colors = ['#10B981', isDark ? '#FFFFFF' : '#111111', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
                const shapes = ['rounded-full', 'rounded-sm', 'rounded-none'];
                const sizes = ['w-2 h-2', 'w-3 h-3', 'w-1.5 h-3', 'w-3 h-1.5'];
                const angle = (i / 24) * 360;
                const distance = 80 + Math.random() * 60;
                const x = Math.cos((angle * Math.PI) / 180) * distance;
                const y = Math.sin((angle * Math.PI) / 180) * distance;
                
                return (
                  <div
                    key={i}
                    className={`absolute ${shapes[i % 3]} ${sizes[i % 4]}`}
                    style={{
                      backgroundColor: colors[i % colors.length],
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      animation: `confettiBurst 1s ease-out ${i * 0.02}s both`,
                      ['--x' as string]: `${x}px`,
                      ['--y' as string]: `${y}px`,
                      ['--rotation' as string]: `${Math.random() * 720 - 360}deg`,
                      ['--fall' as string]: `${100 + Math.random() * 80}px`,
                    }}
                  />
                );
              })}
            </div>
            
            <div 
              className="text-center px-6 py-5 relative z-10"
              style={{ animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
            >
              <div 
                className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4"
                style={{ animation: 'checkPop 0.6s ease-out 0.2s both' }}
              >
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <div className="text-xl font-bold mb-3" style={{ color: confirmTextColor }}>Meeting Booked!</div>
              <div 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl"
                style={{ backgroundColor: confirmCardBg, border: `2px solid ${confirmCardBorder}` }}
              >
                <Calendar className="w-5 h-5 text-emerald-600" />
                <span className="text-base font-semibold text-emerald-700">Thursday at 10:00 AM</span>
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div 
          className="px-4 py-3"
          style={{ backgroundColor: inputAreaBg, borderTop: `1px solid ${borderColor}` }}
        >
          <div 
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{ backgroundColor: inputBg, border: `1px solid ${inputBorder}` }}
          >
            <input 
              type="text" 
              placeholder="AI is handling this conversation..."
              className="flex-1 text-xs bg-transparent outline-none"
              style={{ color: inputText }}
              disabled
            />
            <button 
              className="px-3 py-1 rounded-md text-[10px] font-medium"
              style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111', color: isDark ? '#111111' : '#FFFFFF' }}
            >
              Send
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes checkPop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes confettiBurst {
          0% { 
            transform: translate(-50%, -50%) scale(0) rotate(0deg);
            opacity: 1;
          }
          30% { 
            transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1) rotate(var(--rotation));
            opacity: 1;
          }
          100% { 
            transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y) + var(--fall))) scale(0.3) rotate(calc(var(--rotation) * 2));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedAIMockup;
